import React from 'react'
import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { useCart } from '../../providers/Cart'
import { useSelectedMarket } from '../../providers/Market'
import { useMarketingWages } from '../../hooks/useMarketingWages'
import { MOBILE_TOP_NAV_BREAKPOINT } from '../Layout/constants'
import { MarketPicker } from '../MarketPicker'
import { LineItem } from './LineItem'

const formatCurrency = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

export const CartDrawer: React.FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(`(min-width:${MOBILE_TOP_NAV_BREAKPOINT}px)`)
  const cart = useCart()
  const { market } = useSelectedMarket()
  const { data: skills } = useMarketingWages()

  const wageByName = React.useMemo(() => {
    const map = new Map<string, number>()
    skills?.forEach((s) => {
      if (s.recommended_wage != null) map.set(s.name, s.recommended_wage)
    })
    return map
  }, [skills])

  const subtotal = cart.items.reduce((sum, i) => {
    const rate = wageByName.get(i.skillName)
    return rate != null ? sum + rate * i.hours * i.quantity : sum
  }, 0)

  return (
    <Drawer
      anchor={isDesktop ? 'right' : 'bottom'}
      open={cart.isOpen}
      onClose={cart.close}
      slotProps={{
        paper: {
          sx: {
            width: isDesktop ? 400 : '100%',
            maxWidth: '100%',
            borderTopLeftRadius: isDesktop ? 0 : theme.spacing(2),
            borderTopRightRadius: isDesktop ? 0 : theme.spacing(2),
            maxHeight: isDesktop ? '100%' : '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 800,
            letterSpacing: 0.5,
          }}
        >
          Your roster
        </Typography>
        <IconButton aria-label="Close cart" onClick={cart.close}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider sx={{ borderColor: 'common.black' }} />
      <Stack spacing={1} sx={{ px: 3, py: 2 }}>
        <MarketPicker />
        {!market && (
          <Typography
            variant="body3"
            sx={{
              color: 'text.secondary',
              fontFamily: theme.app.monoFontFamily,
              fontSize: 12,
            }}
          >
            Select your market to see rates and check out.
          </Typography>
        )}
      </Stack>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3 }}>
        {cart.items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              Your roster is empty.
            </Typography>
          </Box>
        ) : (
          cart.items.map((item) => (
            <LineItem
              key={item.skillName}
              item={item}
              hourlyRate={wageByName.get(item.skillName)}
              onUpdate={(patch) => cart.updateItem(item.skillName, patch)}
              onRemove={() => cart.removeItem(item.skillName)}
            />
          ))
        )}
      </Box>
      <Divider sx={{ borderColor: 'common.black' }} />
      <Stack sx={{ p: 3 }} spacing={2}>
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Total
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {formatCurrency(subtotal)}
            </Typography>
          </Stack>
          <Typography
            variant="body3"
            sx={{
              color: 'text.secondary',
              fontFamily: theme.app.monoFontFamily,
              fontSize: 12,
            }}
          >
            A Qwick service fee will be added at checkout.
          </Typography>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={cart.items.length === 0 || !market}
          onClick={cart.openCheckout}
          sx={{
            textTransform: 'uppercase',
            fontFamily: theme.app.monoFontFamily,
            fontWeight: 800,
            letterSpacing: 0.5,
            borderRadius: 1,
            py: 1.5,
          }}
        >
          Submit roster
        </Button>
      </Stack>
    </Drawer>
  )
}
